<template>
    <div class="portfolio-edit">
        <design-2021-portfolio-edit-title-component
            :portfolio-in="portfolioIn"
            :group-in="groupIn"
            @update-tags="updateTags"
            @toggle-worldmap="toggleWorldmap($event)">
        </design-2021-portfolio-edit-title-component>

        <div class="row">
            <div class="col-12 col-md-6 portfolio-categories-wrapper">
                <div class="portfolio-categories apprentice-simple-card">
                    <div class="component-title">
                        <h5 class="title">
                            {{ $t('portfolio.categories') }}
                        </h5>
                    </div>
                    <b-list-group class="portfolio-category-list">
                        <draggable
                            group="categories"
                            ghost-class="drag-ghost"
                            :list="portfolio.categories"
                            :disabled="!groupIn.permissions.can_edit_portfolio"
                            handle=".category-handle"
                            @change="categoryOrderChanged()">
                            <b-list-group-item
                                class="portfolio-category-list-item"
                                v-for="(cat, catKey) in portfolioIn.categories"
                                :key="catKey"
                                @click="updateSelectedPortfolio(cat.id)"
                                active-class="active"
                                :active="cat.id == selectedPortfolioCategory">
                                <div class="category-item">
                                    <div class="move drag-icon"></div>
                                    <div class="content">
                                        <span
                                            class="drag-button"
                                            v-if="groupIn.permissions.can_edit_portfolio">
                                            <b-button
                                                class="btn-drag-icon btn-sm"
                                                variant="none"
                                                @click="$event.stopPropagation()">
                                                <i class="fas fa-fw fa-arrows-alt-v category-handle"></i>
                                            </b-button>
                                        </span>
                                        <div class="category-name">
                                            {{ cat.name }}
                                            <div
                                                class="badges category-badges"
                                                v-if="portfolioIn.worldmap && cat.continent">
                                                <b-badge
                                                    pill
                                                    :style="cssTagColor(cat.color)">
                                                    {{ worldmapData.continents[cat.continent].continent }}
                                                </b-badge>
                                            </div>
                                        </div>

                                        <b-button
                                            class="btn-publish-assignments-toggle"
                                            :class="getAssignmentsPublishSateClass(cat.id)"
                                            :id="`cat-${cat.id}-assignments-publish-toggle`"
                                            variant="primary-icon"
                                            :disabled="!hasAssignments(cat.id)"
                                            @click="assignmentsPublishToggle(cat.id)">
                                        </b-button>

                                        <b-tooltip
                                            :target="`cat-${cat.id}-assignments-publish-toggle`"
                                            placement="top">
                                            {{
                                                !!haveUnPublishedAssignment(cat.id)
                                                    ? $t('portfolio.publish_all_assignments')
                                                    : $t('portfolio.withdraw_publication_for_all_assignments')
                                            }}
                                        </b-tooltip>

                                        <div class="context-nav portfolio-category-item-context-nav">
                                            <b-dropdown
                                                class="ellipsis-nav portfolio-category-context-menu"
                                                v-if="groupIn.permissions.can_edit_portfolio"
                                                variant="primary"
                                                no-caret
                                                right>
                                                <template #button-content>
                                                    <i class="fas fa-fw fa-ellipsis-v"></i>
                                                </template>
                                                <b-dropdown-item
                                                    class="btn-medal nav-context-link-rating"
                                                    v-b-modal="'portfolio-category-rating-' + cat.id"
                                                    @click="$event.stopPropagation()">
                                                    {{ $t('portfolio.category_weighing') }}
                                                </b-dropdown-item>
                                                <b-dropdown-item
                                                    class="btn-edit nav-context-link-edit"
                                                    v-b-modal="'update-portfolio-category-modal-' + cat.id"
                                                    @click="$event.stopPropagation()">
                                                    {{ $t('portfolio.edit_category') }}
                                                </b-dropdown-item>
                                                <b-dropdown-item
                                                    class="nav-context-link-remove"
                                                    @click="removeCategory(cat, $event)"
                                                    :v-b-tooltip="$t('portfolio.delete_category')"
                                                    variant="inverse-danger">
                                                    <i class="btn-remove"></i>{{ $t('portfolio.delete_category') }}
                                                </b-dropdown-item>
                                            </b-dropdown>

                                            <design-2021-portfolio-edit-update-category-modal-component
                                                :category="cat"
                                                :group-id="groupId"
                                                :portfolio-in="portfolioIn">
                                            </design-2021-portfolio-edit-update-category-modal-component>

                                            <design-2021-portfolio-edit-rating-modal-component
                                                :show-grade-options="true"
                                                :object-in="cat"
                                                grade-target="assignments"
                                                :key-name="'portfolio-category-rating-' + cat.id"
                                                grade-type="assignments"
                                                :connections="categoryConnections"
                                                :title="`${$t('portfolio.category_rating')}: ${cat.name}`"
                                                :title-weight="$t('portfolio.assignment_weighing')"
                                                @on-save="saveRating($event)">
                                            </design-2021-portfolio-edit-rating-modal-component>
                                        </div>
                                    </div>
                                </div>
                            </b-list-group-item>
                        </draggable>
                    </b-list-group>
                    <div
                        class="add-category"
                        v-if="groupIn.permissions.can_edit_portfolio">
                        <b-button
                            class="btn-recycle btn-restore-portfolio-category"
                            v-if="$authUser.isAdmin()"
                            variant="outline-primary"
                            @click="showRestorePortfolioCategoryModal">
                            {{ $t('portfolio.recover_category') }}
                        </b-button>

                        <b-button
                            class="btn-add btn-new-portfolio-category"
                            variant="primary"
                            @click="showAddCategoryModal">
                            {{ $t('portfolio.new_category') }}
                        </b-button>

                        <design-2021-portfolio-edit-add-chose-category-modal-component></design-2021-portfolio-edit-add-chose-category-modal-component>

                        <design-2021-portfolio-edit-add-category-modal-component
                            :group-id="groupId"
                            :portfolio-in="portfolioIn"
                            @update-selected-category="updateSelectedPortfolio">
                        </design-2021-portfolio-edit-add-category-modal-component>

                        <design-2021-portfolio-edit-add-copy-category-modal-component
                            :categories-in="portfolio.categories"
                            :portfolio-in="portfolioIn"
                            :group-id="groupId"
                            @update-selected-category="updateSelectedPortfolio">
                        </design-2021-portfolio-edit-add-copy-category-modal-component>

                        <design-2021-portfolio-edit-add-all-copy-category-modal-component
                            :portfolio-in="portfolioIn"
                            @update-selected-category="updateSelectedPortfolio">
                        </design-2021-portfolio-edit-add-all-copy-category-modal-component>

                        <design-2021-portfolio-edit-restore-category-modal-component
                            v-if="$authUser.isAdmin()"
                            :portfolio-in="portfolioIn"
                            :group-in="groupIn"
                            @category-restored="restoreCategory($event)"
                            ref="restore-category-modal">
                        </design-2021-portfolio-edit-restore-category-modal-component>
                    </div>
                </div>
            </div>

            <div class="col-12 col-md-6 portfolio-assignments-wrapper">
                <div class="portfolio-assignments apprentice-simple-card">
                    <div class="component-title">
                        <h5 class="title">
                            {{ $t('portfolio.assignments') }}
                        </h5>
                    </div>

                    <b-list-group
                        class="portfolio-assignments-list"
                        v-if="getSelectedAssignments.length">
                        <draggable
                            group="assignments"
                            ghost-class="drag-ghost"
                            :list="getSelectedAssignments"
                            :disabled="!groupIn.permissions.can_edit_portfolio"
                            handle=".assignment-handle"
                            @change="assignmentOrderChanged(getSelectedAssignments)">
                            <b-list-group-item
                                class="portfolio-assignments-list-item"
                                v-for="(assignment, assKey) in getSelectedAssignments"
                                :key="assignment.id"
                                @click="editAssignment(assignment.id)">
                                <div class="assignment">
                                    <div
                                        class="drag order-1 order-xl-0"
                                        v-if="groupIn.permissions.can_edit_portfolio">
                                        <b-button
                                            variant="none"
                                            @click="$event.stopPropagation()">
                                            <i class="fas fa-fw fa-arrows-alt-v assignment-handle"></i>
                                        </b-button>
                                    </div>

                                    <div class="content order-2 order-xl-1">
                                        <div class="header">
                                            <div class="title">
                                                <div
                                                    class="name"
                                                    :style="
                                                        assignment.assignment_tags.length > 0 &&
                                                        cssVars(assignment.assignment_tags[0].color)
                                                    ">
                                                    {{ assignment.name }}
                                                </div>
                                                <div
                                                    class="badges tto-badges"
                                                    v-if="assignment.assignment_tags.length || portfolioIn.worldmap">
                                                    <b-badge
                                                        v-if="assignment.country"
                                                        pill
                                                        :style="getCategoryColor()">
                                                        {{ worldmapData.countries[assignment.country].country }}
                                                    </b-badge>
                                                    <b-badge
                                                        v-for="tag of assignment.assignment_tags"
                                                        pill
                                                        :style="cssTagColor(tag.color)"
                                                        :key="`${tag.name}-${tag.id}`">
                                                        {{ tag.name }}
                                                    </b-badge>
                                                </div>
                                                <div
                                                    class="assignment-deadline"
                                                    v-if="assignment.deadlines.length >= 1">
                                                    {{ $t('common.deadline') }}:
                                                    {{ assignment.deadlines[0].agenda_item.date_time | date_time_om }}
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            class="sub-assignments"
                                            v-if="assignment.sub_assignments.length">
                                            {{
                                                `${assignment.sub_assignments.length} ${
                                                    assignment.sub_assignments.length === 1
                                                        ? $t('portfolio.partial_assignment')
                                                        : $t('portfolio.partial_assignments')
                                                }`
                                            }}
                                        </div>

                                        <div
                                            class="sub-assignments"
                                            v-else>
                                            {{ $t('portfolio.no_assignments_in_this_category_yet') }}
                                        </div>
                                    </div>

                                    <div class="management-options order-0 order-xl-2">
                                        <div class="publish-option">
                                            <b-button
                                                class="btn-clock btn-databank-share-edit"
                                                v-if="publishedInTheFuture(assignment)"
                                                v-b-tooltip.hover
                                                variant="warning-icon"
                                                :title="`${$t('portfolio.publication_date')}: ${dateFormat(assignment.publishings[0].published_at)}`"
                                                @click="onPublish(assignment, $event)">
                                            </b-button>
                                            <b-button
                                                class="btn-eye btn-databank-share-edit"
                                                v-else-if="assignment.publishings.length"
                                                v-b-tooltip.hover
                                                variant="primary-icon"
                                                :title="$t('portfolio.withdraw_publication')"
                                                @click="onPublish(assignment, $event)">
                                            </b-button>
                                            <b-button
                                                class="btn-eye-closed btn-databank-share-create"
                                                v-else
                                                variant="danger-icon"
                                                @click="onPublish(assignment, $event)"
                                                :v-b-tooltip="$t('portfolio.publish_assignment')">
                                            </b-button>
                                        </div>

                                        <div
                                            class="context-nav portfolio-assignments-item-context-nav"
                                            v-if="groupIn.permissions.can_edit_portfolio">
                                            <b-dropdown
                                                class="ellipsis-nav portfolio-assignment-context-menu"
                                                variant="primary"
                                                no-caret
                                                right>
                                                <template #button-content>
                                                    <i class="fas fa-fw fa-ellipsis-v"></i>
                                                </template>
                                                <b-dropdown-item
                                                    class="btn-share nav-context-link-share"
                                                    v-if="assignment.ab_assignment && assignment.ab_assignment.active"
                                                    @click.prevent.stop="openShareModal(assignment)">
                                                    {{ $t('portfolio.edit_shared_assignment') }}
                                                </b-dropdown-item>
                                                <b-dropdown-item
                                                    class="btn-share nav-context-link-share"
                                                    v-else-if="assignment.shareable"
                                                    @click.prevent.stop="openShareModal(assignment)">
                                                    {{ $t('portfolio.share_assignment_in_database') }}
                                                </b-dropdown-item>
                                                <b-dropdown-item
                                                    class="nav-context-link-somtoday"
                                                    @click.prevent.stop="openSomtodayUrlModal(assignment)">
                                                    <i class="btn-chain-link"></i>{{ $t('portfolio.share_assignment') }}
                                                </b-dropdown-item>
                                                <b-dropdown-item
                                                    class="nav-context-link-update-selected-tag"
                                                    v-if="availableTags.length > 0"
                                                    @click.prevent.stop="showSelectTagsModal(assignment)">
                                                    <i class="fas fa-tag"></i
                                                    >{{
                                                        assignment.assignment_tags.length > 0
                                                            ? $t('portfolio.adjust_tag')
                                                            : $t('portfolio.add_tag')
                                                    }}
                                                </b-dropdown-item>
                                                <b-dropdown-item
                                                    class="nav-context-link-manage-tags"
                                                    v-else
                                                    @click.prevent.stop="showTagsManagementModal">
                                                    <i class="fas fa-tags"></i>{{ $t('portfolio.edit_tags') }}
                                                </b-dropdown-item>
                                                <b-dropdown-item
                                                    class="nav-context-link-manage-ratings"
                                                    v-b-modal="'portfolio-assignment-edit-rating-' + assignment.id"
                                                    @click.prevent.stop>
                                                    <i class="btn-medal"></i>{{ $t('portfolio.set_review') }}
                                                </b-dropdown-item>

                                                <b-dropdown-item
                                                    class="nav-context-link-manage-deadlines"
                                                    @click.preev.prevent.stop="showDeadlineModal(assignment)">
                                                    <i class="btn-clock"></i>{{ $t('portfolio.edit_deadline') }}
                                                </b-dropdown-item>

                                                <b-dropdown-item
                                                    class="nav-context-link-remove"
                                                    @click.prevent.stop="removeAssignment(assignment.id)">
                                                    <i class="btn-remove"></i>{{ $t('portfolio.delete_assignment') }}
                                                </b-dropdown-item>
                                            </b-dropdown>
                                        </div>
                                    </div>
                                </div>

                                <design-2021-portfolio-edit-rating-modal-component
                                    :object-in="assignment"
                                    grade-target="sub_assignments"
                                    :key-name="'portfolio-assignment-edit-rating-' + assignment.id"
                                    :show-grade-options="true"
                                    :connections="[{ type: 'subAssignments', name: 'Deelopdrachten' }]"
                                    :title="$t('assignment.rate_assignment') + ': ' + assignment.name"
                                    :title-weight="$t('portfolio.partial_assignment_weighing')"
                                    @on-save="storeAssignmentRating">
                                </design-2021-portfolio-edit-rating-modal-component>
                            </b-list-group-item>
                        </draggable>

                        <design-2021-portfolio-edit-deadline-modal-component
                            :group-in="groupIn"
                            ref="portfolioEditDeadlineModal"
                            @update-deadline="updateDeadline">
                        </design-2021-portfolio-edit-deadline-modal-component>
                        <design-2021-portfolio-edit-publishing-modal-component
                            :group-in="groupIn"
                            @update-publishing="updatePublishing"
                            ref="assignment-publishing-modal">
                        </design-2021-portfolio-edit-publishing-modal-component>
                    </b-list-group>

                    <div
                        class="not-found with-header empty-portfolio-category-list"
                        v-else>
                        <div
                            class="btn-add-circle icon"
                            v-if="groupIn.permissions.can_edit_portfolio"
                            v-b-tooltip.hover
                            @click="showAddAssignmentModal"
                            :title="$t('portfolio.add_assignment')"></div>
                        <div class="alert-notice">{{ $t('portfolio.this_category_has_no_assignments_yet') }}</div>
                    </div>

                    <design-2021-portfolio-edit-share-modal-component
                        ref="share-assignment-modal"
                        @update-assignments="sharedAssignmentUpdate"
                        @remove-shared-assignments="removeSharedAssignment">
                    </design-2021-portfolio-edit-share-modal-component>

                    <design-2021-portfolio-edit-somtoday-share-link-modal-component ref="somtoday-link-modal">
                    </design-2021-portfolio-edit-somtoday-share-link-modal-component>

                    <div
                        class="new-assignments"
                        v-if="groupIn.permissions.can_edit_portfolio">
                        <b-button
                            class="btn-recycle btn-restore-assignment"
                            v-if="$authUser.isAdmin()"
                            variant="outline-primary"
                            @click="showRestoreAssignmentModal">
                            {{ $t('portfolio.restore_assignment') }}
                        </b-button>

                        <b-button
                            class="btn-add btn-new-assignment"
                            v-if="getSelectedAssignments.length >= 1"
                            variant="primary"
                            @click="showAddAssignmentModal">
                            {{ $t('portfolio.new_assignment') }}
                        </b-button>

                        <design-2021-portfolio-edit-add-new-assignment-modal-component
                            :category="getSelectedCategories()"
                            :key="selectedPortfolioCategory"
                            :portfolio-in="portfolioIn"
                            :course-id="groupIn.course_id"
                            :education-levels="educationLevels"
                            :available-tags="availableTags"
                            :group-id="groupId"
                            @setup-continent="choseContinentForSelectedCategory">
                        </design-2021-portfolio-edit-add-new-assignment-modal-component>

                        <design-2021-portfolio-edit-add-shared-assignment-modal-component
                            :course-id="groupIn.course_id"
                            :education-levels="educationLevels"
                            :group-id="groupId"
                            :portfolio-category-id="selectedPortfolioCategory"
                            @add-assignment-to-list="addSharedAssignmentToAssignmentsList">
                        </design-2021-portfolio-edit-add-shared-assignment-modal-component>

                        <design-2021-portfolio-edit-add-existing-assignment-component
                            :category="getSelectedCategories()"
                            :portfolio-in="portfolioIn"
                            :group-id="groupId">
                        </design-2021-portfolio-edit-add-existing-assignment-component>

                        <design-2021-portfolio-assignment-tag-select-modal-component
                            ref="assignmentTagSelectModal"
                            :group-id="groupIn.id"
                            :available-tags="availableTags"
                            @stored-tag="updateAssignmentTag">
                        </design-2021-portfolio-assignment-tag-select-modal-component>

                        <design-2021-portfolio-edit-restore-assignment-modal-component
                            v-if="$authUser.isAdmin()"
                            :portfolio-in="portfolioIn"
                            :selected-category-id-in="selectedPortfolioCategory"
                            :group-in="groupIn"
                            @assignment-restored="restoreAssignment($event)"
                            ref="restore-assignment-modal"></design-2021-portfolio-edit-restore-assignment-modal-component>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
    import Design2021PortfolioEditAddAllCopyCategoryModalComponent from './modals/design-2021-portfolio-edit-add--all-copy-category-modal-component';
import swal from 'sweetalert';
import { worldmapData } from '../../../helpers/worldmapData';

export default {
    name: 'design-2021-portfolio-edit-component',
    props: ['portfolioIn', 'groupIn', 'groupId', 'educationLevels'],
    components: {
        Design2021PortfolioEditAddAllCopyCategoryModalComponent,
        draggable,
        swal,
    },
    data() {
        return {
            selectedPortfolioCategory: null,
            portfolio: this.portfolioIn,
            categoryConnections: [
                { type: 'assignments', name: this.$t('portfolio.assignments') },
                { type: 'subAssignments', name: this.$t('portfolio.partial_assignments') },
            ],
            removedAssignment: null,
            categories: [],
            availableTags: [],
            bgColor: '',
        };
    },
    methods: {
        restoreAssignment(assignment) {
            this.portfolio.categories
                .find((category) => category.id === this.selectedPortfolioCategory)
                .assignments.push(assignment);
        },

        restoreCategory(category) {
            this.portfolio.categories.push(category);
            this.categoryOrderChanged();
        },

        openShareModal(assignment) {
            this.$refs['share-assignment-modal'].show(assignment, false, [this.groupIn.course_id]);
        },

        updateSelectedPortfolio(id) {
            const name = 'portfolio-show-selected-category-' + this.portfolioIn.id;
            this.selectedPortfolioCategory = id;
            localStorage.setItem(name, id);
        },

        openSomtodayUrlModal(assignment) {
            this.$refs['somtoday-link-modal'].show(assignment.uuid, this.groupIn.uuid);
        },

        removeCategory(c, event) {
            event.stopPropagation();

            this.alert
                .confirm(
                    this.$t('common.delete_item', { item: c.name }),
                    this.$t('portfolio.are_you_sure_you_want_to_delete_this_category'),
                )
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving(
                            this.$t('portfolio.delete_category'),
                            this.$t('portfolio.the_category_will_be_deleted_please_wait_a_moment'),
                        );

                        if (this.selectedPortfolioCategory && this.selectedPortfolioCategory === c.id) {
                            var category = '';
                            for (var i = 0; i < this.portfolioIn.categories.length; i++) {
                                if (this.portfolioIn.categories[i].id !== c.id) {
                                    category = this.portfolioIn.categories[i].id;
                                }
                            }
                            this.updateSelectedPortfolio(category);
                        }

                        this.axios.delete(this.routes.newPortfolioCategoryDelete(this.portfolioIn.id, c.id)).then(
                            (response) => {
                                this.portfolio.categories = this.portfolio.categories.filter(
                                    (category) => category.id !== c.id,
                                );
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },

        getSelectedCategories() {
            if (this.selectedPortfolioCategory !== null) {
                for (const category of this.portfolioIn.categories) {
                    if (category.id == this.selectedPortfolioCategory) {
                        return category;
                    }
                }
            }
        },

        showAddCategoryModal() {
            this.$bvModal.show('add-choose-portfolio-category-modal');
        },

        showRestorePortfolioCategoryModal() {
            this.$refs['restore-category-modal'].show();
        },
        showRestoreAssignmentModal() {
            this.$refs['restore-assignment-modal'].show();
        },

        showAddAssignmentModal() {
            this.$bvModal.show(`portfolio-edit-add-assignment-modal-${this.selectedPortfolioCategory}`);
        },
        onPublish(assignment, event) {
            event.stopPropagation();

            this.$refs['assignment-publishing-modal'].show(assignment);
        },
        updatePublishing(assignment) {
            this.categories = this.categories.map((category) => {
                if (category.id !== assignment.pivot.assignmentable_id) {
                    return category;
                }
                return category.assignments.map((assignment0) => {
                    if (assignment0.id === assignment.id) {
                        assignment0.publishings = assignment.publishings;
                    }
                    return assignment0;
                });
            });
        },
        toggleWorldmap(value) {
            this.portfolio.worldmap = value;
            this.alert.success();
        },

        categoryOrderChanged() {
            let array = [];
            let count = 0;

            for (const category of this.portfolio.categories) {
                count++;
                array.push({
                    id: category.id,
                    order: count,
                });
            }

            const data = {
                portfolio_id: this.portfolio.id,
                blocks: array,
            };

            this.axios.post(this.routes.portfolioCategoryOrder(), data).then(
                (response) => {
                    // no action required
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        assignmentOrderChanged(assignments) {
            let array = [];
            let counter = 0;
            for (let subAssignment of assignments) {
                counter++;
                array.push({
                    id: subAssignment.id,
                    order: counter,
                });
            }

            const data = {
                category_id: this.selectedPortfolioCategory,
                blocks: array,
            };

            this.axios.post(this.routes.portfolioCategoryAssignmentOrder(), data).then(
                (response) => {}, // no action required
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        editAssignment(assignment_id) {
            window.location.href = `${this.routes.assignment()}/edit/${assignment_id}/${this.groupId}`;
        },

        saveRating(data) {
            this.alert.saving();

            let cat = null;
            for (const c of this.portfolio.categories) {
                if (c.id === data.id) {
                    cat = c;
                    break;
                }
            }

            if (!cat.portfolio_rating) {
                this.axios.post(this.routes.portfolioCategoryRating(), data).then(
                    (response) => {
                        let categories = this.portfolio.categories.map((c) => {
                            if (c.id !== data.id) {
                                return c;
                            }

                            let category = Object.assign({}, c);
                            category.portfolio_rating = response.data.rating;
                            category.assignments = category.assignments.map((assignment) => {
                                for (const a of response.data.assignments) {
                                    if (a.id === assignment.id) {
                                        let new_assignment = Object.assign({}, assignment);
                                        new_assignment.portfolio_rating = a.portfolio_rating;
                                        return new_assignment;
                                    }
                                }
                                return assignment;
                            });
                            return category;
                        });

                        let new_portfolio = Object.assign({}, this.portfolio);
                        new_portfolio.categories = categories;
                        this.portfolio = new_portfolio;

                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            } else {
                this.axios.post(this.routes.portfolioCategoryRatingUpdate(), data).then(
                    (response) => {
                        let categories = this.portfolio.categories.map((c) => {
                            if (c.id !== data.id) {
                                return c;
                            }

                            let category = Object.assign({}, c);

                            category.portfolio_rating = response.data.rating;
                            category.assignments = category.assignments.map((assignment) => {
                                for (const a of response.data.assignments) {
                                    if (a.id === assignment.id) {
                                        let new_assignment = Object.assign({}, assignment);
                                        new_assignment.portfolio_rating = a.portfolio_rating;
                                        return new_assignment;
                                    }
                                }
                                return category;
                            });

                            return category;
                        });

                        let new_portfolio = Object.assign({}, this.portfolio);
                        new_portfolio.categories = categories;
                        this.portfolio = new_portfolio;

                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            }
        },

        removeAssignment(id) {
            this.alert
                .confirm(
                    this.$t('portfolio.delete_assignment'),
                    this.$t('portfolio.are_you_sure_you_want_to_delete_this_assignment'),
                )
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();

                        this.axios.delete(this.routes.assignmentDelete(id)).then(
                            (response) => {
                                this.portfolio.categories.map((category) => {
                                    category.assignments = category.assignments.filter(
                                        (assignment) => assignment.id !== id,
                                    );
                                });

                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },

        sharedAssignmentUpdate(ab_assignment) {
            for (const [catIndex, category] of this.portfolio.categories.entries()) {
                if (category.id === this.selectedPortfolioCategory) {
                    for (const [asIndex, assignment] of category.assignments.entries()) {
                        if (assignment.id === ab_assignment.assignment_id) {
                            this.portfolio.categories[catIndex].assignments[asIndex].ab_assignment = ab_assignment;
                            break;
                        }
                    }
                }
            }
        },

        removeSharedAssignment(assignmentId) {
            for (const [catIndex, category] of this.portfolio.categories.entries()) {
                if (category.id === this.selectedPortfolioCategory) {
                    for (const [asIndex, assignment] of category.assignments.entries()) {
                        if (assignment.id === assignmentId) {
                            this.portfolio.categories[catIndex].assignments[asIndex].ab_assignment = null;
                            break;
                        }
                    }
                }
            }
        },

        getAllCategories() {
            axios.get(this.routes.assignmentGetCategoriesByCourse(this.groupIn.course_id)).then(
                (response) => {
                    if (response.data.length) {
                        this.categories = response.data.map((category) => {
                            return {
                                text: category.name,
                                value: category.id,
                            };
                        });

                        this.categories.unshift({
                            text: this.$t('portfolio.choose_category'),
                            value: null,
                            disabled: true,
                        });
                    }
                },
                (error) => {
                    this.alert.ajaxError(error, this.$t('portfolio.error_retrieving_categories'));
                },
            );
        },

        addSharedAssignmentToAssignmentsList(assignment) {
            for (const [catIndex, category] of this.portfolio.categories.entries()) {
                if (category.id === this.selectedPortfolioCategory) {
                    this.portfolio.categories[catIndex].assignments.push(assignment);
                }
            }
        },

        haveUnPublishedAssignment(categoryId) {
            if (this.portfolio && this.portfolio.categories) {
                for (const category of this.portfolio.categories) {
                    if (category.id !== categoryId) {
                        continue;
                    }

                    for (const assignment of category.assignments) {
                        if (assignment.publishings && !assignment.publishings.length) {
                            return true;
                        }
                    }
                }
            }

            return false;
        },

        getAssignmentsPublishSateClass(categoryId) {
            const state = this.getCategoryAssignmentsPublishStates(categoryId);

            if (state.published) {
                return 'bg-primary btn-eye';
            }

            if (state.unPublished) {
                return 'bg-danger btn-eye-closed';
            }

            return 'bg-warning btn-eye-low-vision';
        },

        assignmentsPublishToggle(categoryId) {
            const state = this.getCategoryAssignmentsPublishStates(categoryId);

            if (state.published) {
                this.unpublishCategoryAssignments(categoryId);
            } else if (state.unPublished) {
                this.publishCategoryAssignments(categoryId);
            } else {
                swal({
                    title: this.$t('portfolio.publish_or_withdraw_assignments'),
                    icon: 'warning',
                    text: this.$t(
                        'portfolio.a_part_of_the_assignments_have_been_published_but_some_have_not_what_do_you_want_to_do',
                    ),
                    buttons: {
                        unpublish: {
                            text: this.$t('portfolio.withdraw_publication'),
                            value: 'unpublish',
                            closeModal: close,
                            visible: true,
                            className: 'btn btn-danger',
                        },
                        publish: {
                            text: this.$t('portfolio.publish'),
                            value: 'publish',
                            visible: true,
                            closeModal: close,
                            className: 'btn btn-primary',
                        },
                    },
                }).then((value) => {
                    switch (value) {
                        case 'unpublish':
                            this.removePortfolioCategoryAssignmentsPublishing(categoryId);
                            break;

                        case 'publish':
                            this.storePortfolioCategoryAssignmentsPublishing(categoryId);
                            break;
                    }
                });
            }
        },

        publishCategoryAssignments(categoryId) {
            this.alert
                .confirm(
                    this.$t('portfolio.publish'),
                    this.groupIn.children.length
                        ? this.$t(
                              'portfolio.this_group_has_subgroups_if_you_publish_the_assignments_within_this_category_you_will_also_do_this_for_the_same_category_of_all_subgroups_are_you_sure_you_want_to_do_this',
                          )
                        : this.$t('portfolio.are_you_sure_you_want_to_publish_all_assignments_in_this_category'),
                    this.$t('portfolio.yes_publish'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    this.storePortfolioCategoryAssignmentsPublishing(categoryId, true);
                });
        },

        storePortfolioCategoryAssignmentsPublishing(categoryId, showMessage = false) {
            this.axios.post(this.routes.portfolioCategoryAssignmentsPublishing(categoryId, this.groupIn.id)).then(
                (response) => {
                    if (showMessage) {
                        this.alert.success();
                    }

                    this.portfolio.categories = this.portfolio.categories.map((category) => {
                        if (category.id === categoryId) {
                            category = response.data[0];
                        }

                        return category;
                    });
                },

                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        unpublishCategoryAssignments(categoryId) {
            this.alert
                .confirm(
                    this.$t('portfolio.withdraw_publication'),
                    this.groupIn.children.length
                        ? this.$t(
                              'portfolio.this_group_has_subgroups_if_you_withdraw_the_publication_you_will_also_do_this_for_all_subgroups_are_you_sure_you_want_to_do_this',
                          )
                        : this.$t('portfolio.are_you_sure_you_want_to_withdraw_the_publication_within_this_category'),
                    this.$t('portfolio.yes_withdraw_publication'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    this.removePortfolioCategoryAssignmentsPublishing(categoryId, true);
                });
        },

        removePortfolioCategoryAssignmentsPublishing(categoryId, showMessage = false) {
            this.axios.delete(this.routes.portfolioCategoryAssignmentsPublishing(categoryId, this.groupIn.id)).then(
                (response) => {
                    if (showMessage) {
                        this.alert.success();
                    }
                    this.portfolio.categories = this.portfolio.categories.map((category) => {
                        if (category.id === categoryId) {
                            category = response.data[0];
                        }

                        return category;
                    });
                },

                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        hasAssignments(categoryId) {
            if (this.portfolio && this.portfolio.categories) {
                for (const category of this.portfolio.categories) {
                    if (category.id !== categoryId) {
                        continue;
                    }

                    return category.assignments.length > 0;
                }
            }

            return false;
        },

        getCategoryAssignmentsPublishStates(categoryId) {
            if (this.portfolio && this.portfolio.categories) {
                for (const category of this.portfolio.categories) {
                    if (category.id !== categoryId) {
                        continue;
                    }

                    const maxAssignments = category.assignments.length;
                    let published = 0;
                    let unPublished = 0;
                    for (const assignment of category.assignments) {
                        if (assignment.publishings && assignment.publishings.length) {
                            published++;
                        } else {
                            unPublished++;
                        }
                    }

                    return {
                        published: published === maxAssignments,
                        unPublished: unPublished === maxAssignments,
                    };
                }
            }
        },

        showSelectTagsModal(assignment) {
            this.$refs.assignmentTagSelectModal.show(assignment);
        },

        showTagsManagementModal() {
            this.$bvModal.show('portfolio-tags-management-modal');
        },

        updateAssignmentTag(tag, assignmentId) {
            if (this.selectedPortfolioCategory === null) {
                return;
            }

            for (const category of this.portfolio.categories) {
                if (category.id === this.selectedPortfolioCategory) {
                    category.assignments = category.assignments.map((assignment) => {
                        if (assignment.id === assignmentId) {
                            assignment.assignment_tags = tag.length === 0 ? [] : tag;
                        }

                        return assignment;
                    });
                }
            }
        },

        updateTags(tags) {
            this.availableTags = [...tags];
        },

        cssVars(color) {
            return {
                '--bg-color': color,
            };
        },

        storeAssignmentRating(data) {
            const assignment = this.getSelectedAssignments.find((assignment) => assignment.id === data.id);
            this.alert.saving();

            if (!assignment.portfolio_rating) {
                this.axios.post(this.routes.portfolioAssignmentRating(), data).then(
                    (response) => {
                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            } else {
                this.axios.post(this.routes.portfolioAssignmentRatingUpdate(), data).then(
                    (response) => {
                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            }
        },

        showDeadlineModal(assignment) {
            this.$refs.portfolioEditDeadlineModal.show(assignment);
        },

        updateDeadline(deadline) {
            for (const category of this.portfolio.categories) {
                if (category.id == this.selectedPortfolioCategory) {
                    category.assignments = category.assignments.map((assignment) => {
                        if (assignment.id === deadline.assignment_id) {
                            if (deadline.agenda_item) {
                                assignment.deadlines = [deadline];
                            } else {
                                assignment.deadlines = [];
                            }
                        }

                        return assignment;
                    });
                    return category.assignments;
                }
            }
        },
        publishedInTheFuture(assignment) {
            if (assignment.publishings.length) {
                return new Date(assignment.publishings[0].published_at).getTime() > new Date().getTime();
            }
            return false;
        },
        dateFormat(unformattedDateString) {
            let dateObject = new Date(unformattedDateString);
            return new Intl.DateTimeFormat('nl', {
                dateStyle: 'short',
                timeStyle: 'short',
            }).format(dateObject);
        },

        cssTagColor(color) {
            return {
                '--tagColor': color,
            };
        },

        choseContinentForSelectedCategory() {
            this.$bvModal.show('update-portfolio-category-modal-' + this.selectedPortfolioCategory);
        },

        getCategoryColor() {
            const category = this.portfolio.categories.find((cat) => cat.id === this.selectedPortfolioCategory);

            if (category) {
                return this.cssTagColor(category.color);
            }
        },
    },

    computed: {
        worldmapData() {
            return worldmapData;
        },
        getSelectedAssignments() {
            if (this.selectedPortfolioCategory !== null) {
                for (const category of this.portfolio.categories) {
                    if (category.id === this.selectedPortfolioCategory) {
                        return category.assignments;
                    }
                }
            }

            return [];
        },
    },

    mounted() {
        const name = 'portfolio-show-selected-category-' + this.portfolioIn.id;
        const id = parseInt(localStorage.getItem(name));
        if (id) {
            for (let i = 0; i < this.portfolioIn.categories.length; i++) {
                if (this.portfolioIn.categories[i].id === id) {
                    this.selectedPortfolioCategory = id;
                }
            }
        }

        if (!this.selectedPortfolioCategory) {
            if (this.portfolioIn.categories.length > 0) {
                this.updateSelectedPortfolio(this.portfolioIn.categories[0].id);
            }
        }

        this.getAllCategories();

        this.axios.get(this.routes.getassignmentTags(this.groupId)).then(
            (response) => {
                if (response.data.length > 0) {
                    this.availableTags = response.data.map((tag) => {
                        return {
                            id: tag.id,
                            name: tag.name,
                            color: tag.color,
                            group_id: tag.group_id,
                        };
                    });
                }
            },
            (error) => {
                this.alert.ajaxError(error);
            },
        );
    },

    watch: {
        portfolioIn: {
            handler(value) {
                this.portfolio = value;
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
.portfolio-categories {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.portfolio-category-list {
    .portfolio-category-list-item {
        padding: 0;
        border: none;

        .category-item {
            padding-top: map-get($grid-gutter-spacing, 'eight');
            padding-bottom: map-get($grid-gutter-spacing, 'eight');
            border-radius: $border-radius-content;
            margin-bottom: map-get($grid-gutter-spacing, 'ten');
            border: 1px solid $white;

            &:hover {
                cursor: pointer;
                border-radius: $border-radius-content;
                border: 1px solid var(--primary);
            }
        }

        &:not(:first-child) {
            .category-item {
                margin-top: map-get($grid-gutter-spacing, 'ten');
            }
        }

        &:not(:last-child) {
            border-bottom: 3px dotted $border-color;
        }

        &.active {
            background-color: transparent;

            .category-item {
                background-color: $secondary-background-color;
                font-weight: $font-weight-bold;
                color: $text-color;
            }

            .btn-drag-icon {
                color: $text-color;
            }
        }
    }

    .content {
        display: flex;
        align-items: center;
        flex-direction: row;

        .category-name {
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }

        .btn-publish-assignments-toggle {
            margin-left: auto;
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
            line-height: 1.5;
            font-size: $base-font-size;

            &:before {
                margin-right: 0;
            }

            &.disabled {
                &,
                &:hover {
                    background-color: darken($border-color, 10%) !important;

                    &:before {
                        color: $white;
                    }
                }
            }
        }

        .portfolio-category-item-context-nav ::v-deep {
            margin-right: map-get($grid-gutter-spacing, 'fifteen');

            .portfolio-category-context-menu {
                button.dropdown-toggle {
                    background-color: var(--primary);
                    color: $white !important;
                }

                .dropdown-menu {
                    border: 1px solid $border-color;
                }

                li.nav-context-link-rating,
                li.nav-context-link-edit,
                li.nav-context-link-remove {
                    &:before {
                        color: var(--primary);
                    }
                }

                li.nav-context-link-remove {
                    &:before {
                        color: $danger;
                    }
                }

                li.nav-context-link-remove a {
                    display: flex;
                    align-items: center;

                    i {
                        margin-right: map-get($grid-gutter-spacing, 'eight');
                    }
                }
            }
        }
    }
}

.portfolio-assignments-list {
    .portfolio-assignments-list-item {
        border: none;
        padding-left: 0;
        padding-right: 0;

        &:first-child {
            padding-top: 0;
        }

        &:not(:first-child) {
            .assignment {
                margin-top: map-get($grid-gutter-spacing, 'ten');
            }
        }

        &:not(:last-child) {
            border-bottom: 3px dotted $border-color;
        }
    }

    .assignment {
        display: flex;
        align-items: center;
        flex-direction: row;
        flex-wrap: wrap;
        padding-top: map-get($grid-gutter-spacing, 'eight');
        padding-bottom: map-get($grid-gutter-spacing, 'eight');
        border-radius: $border-radius-content;
        border: 1px solid $white;

        .management-options {
            display: flex;
            flex-direction: row;
            flex: 1 0 100%;
            margin-bottom: map-get($grid-gutter-spacing, 'ten');
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
            justify-content: flex-end;

            .publish-option {
                .btn-databank-share-create,
                .btn-databank-share-edit {
                    font-size: $base-font-size;
                    line-height: 1.5;

                    &:before {
                        margin-right: 0;
                    }
                }
            }

            .portfolio-assignments-item-context-nav ::v-deep {
                margin-left: map-get($grid-gutter-spacing, 'fifteen');

                .portfolio-assignment-context-menu {
                    button.dropdown-toggle {
                        background-color: var(--primary);
                        color: $white !important;
                    }

                    .dropdown-menu {
                        border: 1px solid $border-color;
                    }

                    li.nav-context-link-share,
                    li.nav-context-link-share,
                    .nav-context-link-update-selected-tag,
                    .nav-context-link-manage-tags,
                    .nav-context-link-manage-ratings,
                    .nav-context-link-manage-deadlines {
                        i,
                        &:before {
                            color: var(--primary);
                        }
                    }

                    li.nav-context-link-remove {
                        &:before {
                            color: $danger;
                        }
                    }

                    li.nav-context-link-somtoday a,
                    li.nav-context-link-remove a {
                        display: flex;
                        align-items: center;

                        i {
                            margin-right: map-get($grid-gutter-spacing, 'eight');
                        }
                    }
                }
            }
        }

        .content {
            .header {
                justify-content: flex-start;
            }

            .title {
                display: flex;
                flex-direction: column;
                font-size: 1rem;
                line-height: 1;
                font-weight: $font-weight-bold;
                row-gap: map-get($grid-gutter-spacing, 'four');
                flex-wrap: wrap;
                justify-self: flex-start;

                .name {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: flex-start;
                    column-gap: $grid-gutter-width;
                }

                .assignment-deadline {
                    color: $text-muted-color;
                }
            }

            .sub-assignments {
                color: $text-muted-color;
            }
        }

        &:not(:last-child) {
            border-bottom: 3px dotted $border-color;
        }

        &:hover {
            cursor: pointer;
            border-radius: $border-radius-content;
            border: 1px solid var(--primary);
            font-weight: $font-weight-bold;

            .sub-assignments {
                font-weight: $font-weight-normal;
            }
        }
    }
}

.add-category,
.new-assignments {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    row-gap: map-get($grid-gutter-spacing, 'twenty-one');
    margin-top: map-get($grid-gutter-spacing, 'forty-two');

    button {
        width: 100%;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .add-category,
    .new-assignments {
        justify-content: flex-start;
        flex-direction: row;
        column-gap: $grid-gutter-width;

        button {
            width: auto;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .portfolio-categories {
        margin-bottom: 0;
    }

    .portfolio-assignments,
    .portfolio-categories {
        min-height: 310px;
    }

    .add-category,
    .new-assignments {
        justify-content: flex-start;
        flex-direction: column;
        column-gap: 0;
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .portfolio-assignments-list {
        .assignment {
            .management-options {
                flex: 0 0 auto;
                margin-bottom: 0;
                margin-left: auto;
                margin-right: 0;
                align-self: flex-start;
            }

            .content {
                max-width: calc(100% - 133px);

                .title {
                    column-gap: map-get($grid-gutter-spacing, 'ten');

                    .assignment-deadline {
                        flex: 1 0 100%;
                    }
                }
            }
        }
    }

    .portfolio-assignments-list-item {
        .assignment {
            padding-right: map-get($grid-gutter-spacing, 'fifteen');
        }
    }

    .portfolio-assignments,
    .portfolio-categories {
        min-height: 255px;
    }

    .add-category,
    .new-assignments {
        flex-direction: row;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
    }
}

@media (min-width: map-get($grid-breakpoints, 'xxl')) {
    .add-category,
    .new-assignments {
        flex-direction: row;
        column-gap: $grid-gutter-width;
    }
}
</style>
